import { useEffect, useMemo, useRef } from 'react'

import { useBoundStore } from '../store/store'
import type { ApiResponse } from '../types/api'
import type { UserInfo } from '../types/user'
import type { AdjustAttributionMeta } from '../utils/adjust'
import { getAdjustAttribution, getAdjustWebUUID } from '../utils/adjust'
import { getAdsInfo } from '../utils/ads-info'
import { api } from '../utils/api-client'
import { getDeviceInfo } from '../utils/device'
import { getDeviceId } from '../utils/device-id'
import { syncFcmToken } from '../utils/fcmToken'
import {
  clearFirstLandingUrl,
  getFirstLandingUrl,
} from '../utils/first-landing-url'
import { getPublicAuth } from '../utils/getPublicAuth'
import { nativeSupportsOAuth } from '../utils/nativeBridge'
import {
  isReferralCodeError,
  storeReferralError,
} from '../utils/referral-error'
import { trackRegistrationSuccess } from '../utils/registration-tracking'
import { encryptUser } from '../utils/user'
import { watchPopupClosed } from '../utils/watchPopupClosed'

const OAUTH_POLL_INTERVAL_MS = 1500
const OAUTH_POLL_MAX_ITERATIONS = 120 // ~3 minutes at 1.5s cadence
const POLLS_AFTER_POPUP_CLOSED = 3

const idTokenError = (provider: 'google' | 'apple') =>
  `No se pudo completar el inicio de sesión con ${
    provider === 'google' ? 'Google' : 'Apple'
  }. Inténtalo de nuevo.`

// The authenticate -> popup -> poll flow; Apple (below) uses its own.
const POLL_PROVIDERS = [
  'facebook',
  'telegram',
  'google',
  'twitter',
  'twitch',
  'tiktok',
] as const

type PollProvider = (typeof POLL_PROVIDERS)[number]

export const LOBBY_OAUTH_PROVIDERS = [...POLL_PROVIDERS, 'apple'] as const

export type LobbyOAuthProvider = (typeof LOBBY_OAUTH_PROVIDERS)[number]

export const isLobbyOAuthProvider = (v: unknown): v is LobbyOAuthProvider =>
  typeof v === 'string' &&
  (LOBBY_OAUTH_PROVIDERS as readonly string[]).includes(v)

// Same success shape callback.php returns as the login page's OAuth flow.
type OAuthLoginInfo = UserInfo & {
  password?: string
  is_new_register?: number
}

interface UseOAuthLoginOptions {
  // The attempt ended (cancel/fail/timeout); the caller reloads the iframe.
  onSettled?: () => void
  // OAuth couldn't start in this environment; the caller degrades to loginNeed.
  onCannotStart?: () => void
  onError?: (message: string) => void
}

// Runs OAuth in the top-level parent for the lobby iframe, which can't itself.
export const useOAuthLogin = ({
  onSettled,
  onCannotStart,
  onError,
}: UseOAuthLoginOptions = {}) => {
  const setUserStore = useBoundStore(state => state.user.setUser)
  const enableFacebook = useBoundStore(state => state.settings.login_facebook)
  const enableTelegram = useBoundStore(state => state.settings.login_telegram)
  const enableGoogle = useBoundStore(state => state.settings.login_google)
  const enableTwitter = useBoundStore(state => state.settings.login_twitter)
  const enableTwitch = useBoundStore(state => state.settings.login_twitch)
  const enableTiktok = useBoundStore(state => state.settings.login_tiktok)
  const enableIos = useBoundStore(state => state.settings.login_ios)

  const enabled: Record<PollProvider, boolean> = {
    facebook: !!enableFacebook.value,
    telegram: !!enableTelegram.value,
    google: !!enableGoogle.value,
    twitter: !!enableTwitter.value,
    twitch: !!enableTwitch.value,
    tiktok: !!enableTiktok.value,
  }

  // Apple shows with a native bridge or the login_ios flag (as on the login page).
  const appleEnabled = !!window.Fun88Native?.appleSignIn || !!enableIos.value

  const busyRef = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptRef = useRef(0)
  const iterationRef = useRef(0)
  const capRef = useRef(OAUTH_POLL_MAX_ITERATIONS)
  const stopPopupWatchRef = useRef<(() => void) | null>(null)
  // Detaches the Apple SDK listeners; in a ref so unmount can run it.
  const appleCleanupRef = useRef<(() => void) | null>(null)
  const params = useMemo(() => new URLSearchParams(window.location.search), [])

  // Stop an in-flight flow if the host unmounts.
  useEffect(
    () => () => {
      busyRef.current = false
      attemptRef.current += 1
      if (timerRef.current) clearTimeout(timerRef.current)
      stopPopupWatchRef.current?.()
      appleCleanupRef.current?.()
    },
    [],
  )

  /** Takes the flow over from any in-flight attempt. See docs/memory/oauth-popup-cancel-and-supersede.md */
  const beginAttempt = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    stopPopupWatchRef.current?.()
    stopPopupWatchRef.current = null
    appleCleanupRef.current?.()
    attemptRef.current += 1
    iterationRef.current = 0
    capRef.current = OAUTH_POLL_MAX_ITERATIONS
    busyRef.current = true
    return attemptRef.current
  }

  // Idempotent: the Apple listener and its promise can both end one attempt.
  const settle = () => {
    if (!busyRef.current) return
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    stopPopupWatchRef.current?.()
    stopPopupWatchRef.current = null
    busyRef.current = false
    onSettled?.()
  }

  const cannotStart = () => {
    if (!busyRef.current) return
    stopPopupWatchRef.current?.()
    stopPopupWatchRef.current = null
    busyRef.current = false
    onCannotStart?.()
  }

  /** Refused before an attempt began, so it must not touch the in-flight attempt's busyRef. */
  const rejectStart = () => onCannotStart?.()

  const finishOAuthLogin = async (info: OAuthLoginInfo): Promise<boolean> => {
    const password = info.password as string
    const publicAuth = await getPublicAuth()
    const privateAuth = await api.post<
      ApiResponse<string | { auth: string; expired_time: number }>
    >('/link.php', {
      username: info.username,
      password,
      auth: publicAuth,
    })
    if (
      privateAuth.data.status !== 1 ||
      typeof privateAuth.data.info === 'string'
    )
      return false

    localStorage.setItem('privateAuth', privateAuth.data.info.auth)
    encryptUser({ username: info.username, password })
    const isNewRegister = info.is_new_register === 1
    delete info.password
    delete info.is_new_register
    setUserStore(info)
    void syncFcmToken()
    // The register page never runs here, so its analytics must fire from here.
    if (isNewRegister) {
      trackRegistrationSuccess(info)
      localStorage.removeItem('ads_info')
      clearFirstLandingUrl()
    }
    // Close the in-app system browser and return; no-op on web.
    window.Fun88Native?.returnToApp?.()
    return true
  }

  const poll = async (
    hash: string,
    provider: LobbyOAuthProvider,
    adjust: { webUuid: string; attribution: Partial<AdjustAttributionMeta> },
    attempt: number,
    iteration = 0,
  ): Promise<void> => {
    if (!busyRef.current || attempt !== attemptRef.current) return
    iterationRef.current = iteration
    if (iteration >= capRef.current) {
      settle()
      return
    }
    try {
      const deviceId = await getDeviceId()
      const adsInfo = getAdsInfo()
      const response = await api.post<ApiResponse<OAuthLoginInfo | string>>(
        `/oauth/${provider}/callback.php`,
        {
          auth_id: hash,
          action: 'login',
          submit_type: `${provider}_oauth`,
          // Registration reads it here; the meta copy is only recorded.
          ...(adsInfo?.referral ? { referral_code: adsInfo.referral } : {}),
          meta: {
            ...getDeviceInfo(),
            ...Object.fromEntries(params.entries()),
            ...(adsInfo ? adsInfo : {}),
            landing_url: getFirstLandingUrl(),
            device_id: adjust.webUuid || deviceId,
            web_uuid: adjust.webUuid,
            ...adjust.attribution,
          },
        },
      )
      if (!busyRef.current || attempt !== attemptRef.current) return
      const { status, info } = response.data
      if ((status === 1 || status === 2) && typeof info !== 'string') {
        // Set the user before settling so the reload fetches a member ticket.
        await finishOAuthLogin(info)
        settle()
        return
      }
      // Same status as an unfinished popup: see docs/memory/oauth-auto-register-tracking.md
      if (typeof info === 'string' && isReferralCodeError(info)) {
        storeReferralError(info)
        cannotStart()
        return
      }
      // status 0 = popup not finished yet; keep polling.
    } catch (error) {
      // A transient hop must not kill the poll; the cap ends it.
      console.error(error)
    }

    timerRef.current = setTimeout(() => {
      void poll(hash, provider, adjust, attempt, iteration + 1)
    }, OAUTH_POLL_INTERVAL_MS)
  }

  // Apple (bridge/SDK) and native Google: id_token -> native_login.php.
  const idTokenLogin = async (
    provider: 'google' | 'apple',
    idToken: string,
    attempt: number,
  ) => {
    try {
      const deviceId = await getDeviceId()
      const adsInfo = getAdsInfo()
      const [webUuid, attribution] = await Promise.all([
        getAdjustWebUUID(),
        getAdjustAttribution(),
      ])
      const response = await api.post<ApiResponse<OAuthLoginInfo | string>>(
        `/oauth/${provider}/native_login.php`,
        {
          id_token: idToken,
          action: 'login',
          submit_type: `${provider}_oauth`,
          ...(adsInfo?.referral ? { referral_code: adsInfo.referral } : {}),
          meta: {
            ...getDeviceInfo(),
            ...Object.fromEntries(params.entries()),
            ...(adsInfo ? adsInfo : {}),
            landing_url: getFirstLandingUrl(),
            device_id: webUuid || deviceId,
            web_uuid: webUuid,
            ...attribution,
          },
        },
      )
      const { status, info } = response.data
      if ((status === 1 || status === 2) && typeof info !== 'string')
        await finishOAuthLogin(info)
      else if (typeof info === 'string' && isReferralCodeError(info)) {
        // Same rejection as the polled flow.
        storeReferralError(info)
        if (attempt === attemptRef.current) cannotStart()
        return
      } else onError?.(typeof info === 'string' ? info : idTokenError(provider))
    } catch (error) {
      console.error(error)
      onError?.(idTokenError(provider))
    }
    // Either outcome reloads the iframe.
    if (attempt === attemptRef.current) settle()
  }

  // Lite-app native sign-in: the bridge returns an id_token, no browser.
  const nativeSignIn = async (provider: 'google' | 'apple', attempt: number) => {
    const signIn =
      provider === 'google'
        ? window.Fun88Native?.googleSignIn
        : window.Fun88Native?.appleSignIn
    if (!signIn) {
      settle()
      return
    }
    try {
      const { idToken, error } = await signIn()
      if (attempt !== attemptRef.current) return
      if (!idToken) {
        // A cancelled sheet is silent; anything else is a real failure.
        if (error !== 'cancelled') onError?.(idTokenError(provider))
        settle()
        return
      }
      await idTokenLogin(provider, idToken, attempt)
    } catch (error) {
      console.error(error)
      if (attempt === attemptRef.current) settle()
    }
  }

  const startApple = async (attempt: number) => {
    // iOS lite app: native Apple sign-in, no popup.
    if (window.Fun88Native?.appleSignIn) {
      await nativeSignIn('apple', attempt)
      return
    }

    // ⚠ Attach the Apple SDK listeners per-attempt only: the login page keeps its own.
    if (!window.AppleID?.auth) {
      cannotStart()
      return
    }
    const onSuccess = (event: Event) => {
      appleCleanupRef.current?.()
      if (attempt !== attemptRef.current) return
      const { authorization } = (
        event as unknown as { detail: { authorization: { id_token: string } } }
      ).detail
      void idTokenLogin('apple', authorization.id_token, attempt)
    }
    const onFailure = () => {
      appleCleanupRef.current?.()
      if (attempt !== attemptRef.current) return
      // The reload is the feedback; no toast for a closed popup.
      settle()
    }
    appleCleanupRef.current = () => {
      appleCleanupRef.current = null
      document.removeEventListener('AppleIDSignInOnSuccess', onSuccess)
      document.removeEventListener('AppleIDSignInOnFailure', onFailure)
    }
    document.addEventListener('AppleIDSignInOnSuccess', onSuccess)
    document.addEventListener('AppleIDSignInOnFailure', onFailure)
    // No poll cap behind this path: an SDK that never opens must exit here.
    try {
      window.AppleID.auth.init({
        clientId: 'com.fun88.mx.client',
        scope: 'name email',
        redirectURI: window.location.origin,
        state: '',
        nonce: '',
        usePopup: true,
      })
      const started = window.AppleID.auth.signIn() as unknown
      // Backstop for a rejection the failure listener never sees.
      if (started instanceof Promise)
        started.catch((error: unknown) => {
          console.error(error)
          appleCleanupRef.current?.()
          if (attempt === attemptRef.current) settle()
        })
    } catch (error) {
      // Never started: degrade like a blocked popup.
      console.error(error)
      appleCleanupRef.current?.()
      if (attempt === attemptRef.current) cannotStart()
    }
  }

  const startOAuthLogin = async (provider: string) => {
    // Drop unknown/disabled providers silently (contract §3).
    if (!isLobbyOAuthProvider(provider)) return

    if (provider === 'apple') {
      if (!appleEnabled) {
        rejectStart()
        return
      }
      void startApple(beginAttempt())
      return
    }

    if (!enabled[provider]) {
      rejectStart()
      return
    }
    const attempt = beginAttempt()

    // Native Google (lite app): skips the Android "add account" interstitial.
    if (provider === 'google' && window.Fun88Native?.googleSignIn) {
      void nativeSignIn('google', attempt)
      return
    }

    try {
      // Lite app: 302 the callback back to the app.
      const redirectScheme = window.Fun88Native?.oauthRedirectUrl
      const response = await api.get<
        ApiResponse<{ url: string; id: string } | string>
      >(
        `/oauth/${provider}/authenticate.php`,
        redirectScheme ? { redirect_scheme: redirectScheme } : undefined,
      )
      const { info } = response.data
      if (attempt !== attemptRef.current) return
      if (typeof info === 'string') {
        cannotStart()
        return
      }

      // In-app shell takes over navigation; else the parent opens a popup.
      if (nativeSupportsOAuth()) {
        window.location.href = info.url
      } else {
        const popup = window.open(info.url, '_blank')
        // A blocked popup can't be retried in place; degrade to loginNeed.
        if (!popup) {
          cannotStart()
          return
        }
        stopPopupWatchRef.current = watchPopupClosed(popup, () => {
          if (attempt !== attemptRef.current) return
          capRef.current = Math.min(
            capRef.current,
            iterationRef.current + POLLS_AFTER_POPUP_CLOSED,
          )
        })
      }

      const [webUuid, attribution] = await Promise.all([
        getAdjustWebUUID(),
        getAdjustAttribution(),
      ])
      if (attempt !== attemptRef.current) return
      void poll(info.id, provider, { webUuid, attribution }, attempt)
    } catch (error) {
      console.error(error)
      if (attempt === attemptRef.current) cannotStart()
    }
  }

  return { startOAuthLogin }
}
